/**
 * Guided-tip content (UX-GUIDED-TIPS-001).
 *
 * A tip explains the current screen: what it is and does (taken from the
 * canonical NAV_HELP entry, so tips and sidebar help cannot drift), plus the
 * primary action, the risk, and a next step. Tips are documentation only:
 * visibility mirrors Layout's nav visibility and grants no access.
 */
import { NAV_HELP } from "./navHelpContent";
import { findTopic } from "./helpTopics";

export const TIPS = {
  "/dashboard": {
    primary: "עברו על הקריאות והמשימות הפתוחות כדי להחליט במה להתחיל.",
    risk: "אין פעולות מסוכנות במסך הזה — הוא לצפייה בלבד.",
    next: "פתחו קריאה מהרשימה כדי לטפל בה.",
    helpTopic: "navigation",
  },
  "/tickets": {
    primary: "פתחו קריאה כדי לראות את הפרטים, לשייך ולטפל בה.",
    risk: "שליחת תגובה ללקוח גלויה ללקוח. הצעות של סוכן AI אינן נשלחות ללא אישור אנושי.",
    next: "בדקו אם יש ריצת AI לקריאה ובחנו את ההצעה לפני שמאשרים.",
    helpTopic: "tickets",
  },
  "/agents": {
    audience: { agentPermission: "agents_view" },
    primary: "בחרו סוכן כדי לראות את ההגדרות והפעילות שלו.",
    risk: "שינוי הגדרות סוכן משפיע על מה שהוא מציע; הרשאות פעולה נקבעות בנפרד ודורשות אישור.",
    next: "עברו למסך ריצות AI כדי לראות מה הסוכן עשה.",
    helpTopic: "agents",
  },
  "/agent-runs": {
    audience: { agentPermission: "agents_view" },
    primary: "פתחו ריצה כדי לראות את הצעדים, ההצעה והסטטוס שלה.",
    risk: "פעולה ממתינה נדרשת לאישור אנושי — בדקו אותה בקפידה לפני שמאשרים.",
    next: "אשרו או דחו רק לאחר שקראתם מה בדיוק תתבצע.",
    helpTopic: "approvals",
  },
  "/command-center": {
    audience: { agentPermission: "agents_view" },
    primary: "עיינו במצב הסוכנים והפעילות; שימו לב לסימון מקור הנתונים.",
    risk: "חלק מהנתונים הם הדגמה. המסך לקריאה בלבד ואינו מבצע פעולות.",
    next: "למידע מדויק עברו למסכי סוכנים וריצות AI.",
    helpTopic: "command-center",
  },
  "/agent-permissions": {
    audience: { agentPermission: "agents_manage" },
    primary: "קבעו מה כל סוכן רשאי לראות ולבצע.",
    risk: "הרחבת הרשאות מגדילה את מה שסוכן יכול להציע לבצע. הפעולות הרגישות עדיין דורשות אישור אנושי.",
    next: "הרחיבו הרשאות רק לפי הצורך ובדקו את יומן הפעולות.",
    helpTopic: "approvals",
  },
  "/connections": {
    audience: { adminOnly: true },
    primary: "נהלו את החיבורים שהסוכנים משתמשים בהם.",
    risk: "חיבור מגדיר לאן סוכן יכול להגיע. סודות אינם מוצגים במסך.",
    next: "ודאו שהחיבור הכרחי ושמוגדרת לו הרשאה מינימלית.",
    helpTopic: "remote-access",
  },
  "/skills": {
    audience: { adminOnly: true },
    primary: "עיינו בדירוג ביצועי הסוכנים לפי שיעור פתרון, משך ועלות.",
    risk: "התצוגה לקריאה בלבד; הדירוג אינו מעניק ולא מסיר הרשאות.",
    next: "בדקו במסך איכות AI את המשוב על תשובות.",
    helpTopic: "skills",
  },
  "/system-map": {
    // This branch gates /system-map behind agents_view (see Layout's NAV), so
    // the tip must not surface to a user who cannot open the screen it describes.
    audience: { agentPermission: "agents_view" },
    primary: "עיינו בשכבות המערכת ובסימון פעיל / מתוכנן.",
    risk: "המפה היא תיעוד בלבד ואינה משנה הרשאות.",
    next: "פתחו את מרכז העזרה לפירוט על כל נושא.",
    helpTopic: "system-map",
  },
  "/help": {
    primary: "חפשו נושא או שאלו שאלה על אופן פעולת המערכת.",
    risk: "אין סיכון — התשובות מצוטטות ממסמכי עזרה מאושרים.",
    next: "כאן גם אפשר לכבות או להפעיל מחדש טיפים מודרכים.",
    helpTopic: "help-center",
  },
};

/** Same rule as Layout's nav filter (agentPermission wins over adminOnly). */
export function canSeeTip(tip, { isAdmin, canAgent }) {
  const a = tip.audience || {};
  if (a.agentPermission) return Boolean(canAgent && canAgent(a.agentPermission));
  if (a.adminOnly) return Boolean(isAdmin);
  return true;
}

/**
 * The tip for a path, or null when there is none or the user may not see the
 * screen it describes. `role` is used for topic links so a technician is never
 * pointed at an admin-only help topic.
 */
export function tipForPath(pathname, { isAdmin, canAgent, role }) {
  const tip = TIPS[pathname];
  if (!tip || !canSeeTip(tip, { isAdmin, canAgent })) return null;
  const help = NAV_HELP[pathname];
  if (!help) return null;
  return {
    route: pathname,
    what: help.what,
    does: help.does,
    primary: tip.primary,
    risk: tip.risk,
    next: tip.next,
    readOnly: help.readOnly,
    helpTopic: findTopic(tip.helpTopic, role) ? tip.helpTopic : null,
  };
}
